import { useState, useEffect, useRef } from "react";
import { authAxios } from "../utils/auth";
import { X, Copy, Send, ArrowRight, ArrowDown, MessageCircle, Sparkles, ChevronRight, RefreshCw, Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "../contexts/ThemeContext";

// Helper to strip emojis from text
const stripEmojis = (text) => {
  if (!text) return "";
  return text.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]|[\u{231A}-\u{231B}]|[\u{23E9}-\u{23F3}]|[\u{23F8}-\u{23FA}]|[\u{25AA}-\u{25AB}]|[\u{25B6}]|[\u{25C0}]|[\u{25FB}-\u{25FE}]|[\u{2614}-\u{2615}]|[\u{2648}-\u{2653}]|[\u{267F}]|[\u{2693}]|[\u{26A1}]|[\u{26AA}-\u{26AB}]|[\u{26BD}-\u{26BE}]|[\u{26C4}-\u{26C5}]|[\u{26CE}]|[\u{26D4}]|[\u{26EA}]|[\u{26F2}-\u{26F3}]|[\u{26F5}]|[\u{26FA}]|[\u{26FD}]|[\u{2702}]|[\u{2705}]|[\u{2708}-\u{270D}]|[\u{270F}]|[\u{2712}]|[\u{2714}]|[\u{2716}]|[\u{271D}]|[\u{2721}]|[\u{2728}]|[\u{2733}-\u{2734}]|[\u{2744}]|[\u{2747}]|[\u{274C}]|[\u{274E}]|[\u{2753}-\u{2755}]|[\u{2757}]|[\u{2763}-\u{2764}]|[\u{2795}-\u{2797}]|[\u{27A1}]|[\u{27B0}]|[\u{27BF}]|[\u{2934}-\u{2935}]|[\u{2B05}-\u{2B07}]|[\u{2B1B}-\u{2B1C}]|[\u{2B50}]|[\u{2B55}]|[\u{3030}]|[\u{303D}]|[\u{3297}]|[\u{3299}]/gu, '').trim();
};

// Helper to clean and render text with **bold** and strip [CC> <CC] tags
// Helper to clean JSON from text (when LLM returns JSON in response)
const cleanJsonFromText = (text) => {
  if (!text) return "";
  
  // Check if text starts with JSON structure
  const trimmed = text.trim();
  if (trimmed.startsWith('{') && trimmed.includes('"insights"')) {
    try {
      const parsed = JSON.parse(trimmed);
      return parsed.insights || text;
    } catch (e) {
      // Try to extract just the insights value
      const insightsMatch = trimmed.match(/"insights"\s*:\s*"([^"]+(?:\\.[^"]*)*?)"/);
      if (insightsMatch) {
        return insightsMatch[1].replace(/\\"/g, '"').replace(/\\n/g, '\n');
      }
    }
  }
  
  // Check if JSON is embedded in text
  const jsonMatch = text.match(/\{[\s\S]*"insights"[\s\S]*\}/);
  if (jsonMatch) {
    try {
      const parsed = JSON.parse(jsonMatch[0]);
      // Return the text before JSON + parsed insights
      const beforeJson = text.substring(0, text.indexOf(jsonMatch[0])).trim();
      return beforeJson ? beforeJson + "\n\n" + parsed.insights : parsed.insights;
    } catch (e) {
      // Remove the JSON block entirely if we can't parse it
      return text.replace(/\{[\s\S]*"insights"[\s\S]*\}/, '').trim();
    }
  }
  
  return text;
};

// Helper to parse unified V2 coaching response (insights, annotations, suggestions)
const parseV2CoachingResponse = (responseText) => {
  const result = {
    insights: "",
    annotations: [],
    suggestions: []
  };
  
  if (!responseText) return result;
  
  const trimmed = responseText.trim();
  
  // Try to parse as JSON
  try {
    // Check if it's a JSON response
    if (trimmed.startsWith('{')) {
      const parsed = JSON.parse(trimmed);
      result.insights = parsed.insights || "";
      result.annotations = Array.isArray(parsed.annotations) ? parsed.annotations : [];
      result.suggestions = Array.isArray(parsed.suggestions) ? parsed.suggestions : [];
      return result;
    }
  } catch (e) {
    // Try to extract JSON from text
    const jsonMatch = trimmed.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[0]);
        result.insights = parsed.insights || "";
        result.annotations = Array.isArray(parsed.annotations) ? parsed.annotations : [];
        result.suggestions = Array.isArray(parsed.suggestions) ? parsed.suggestions : [];
        return result;
      } catch (e2) {
        // Fall through to text parsing
      }
    }
  }
  
  // If not JSON, treat as plain text insights
  result.insights = trimmed;
  return result;
};

const cleanCoachText = (text) => {
  if (!text) return "";
  // First clean any JSON, then do other cleanup
  let cleaned = cleanJsonFromText(text);
  return cleaned
    .replace(/\[CC>[\s\S]*?<CC\]/g, '')  // Remove entire [CC>...<CC] blocks
    .replace(/\[CC>/g, '')  // Cleanup any orphaned opening tags
    .replace(/<CC\]/g, '')  // Cleanup any orphaned closing tags
    .replace(/—/g, '-')  // Replace em dashes with regular dashes
    .replace(/–/g, '-')  // Replace en dashes with regular dashes
    .replace(/---+/g, '') // Remove separator lines
    .replace(/\n---\n/g, '\n') // Remove markdown separators
    .replace(/\n\n\n+/g, '\n\n') // Collapse multiple newlines
    .trim();
};

// Helper to render text with **bold** only (for chat messages)
const renderBoldText = (text, isCyber) => {
  if (!text) return null;
  
  // Clean the text first
  let cleanText = cleanCoachText(text);
  
  // Parse bold only
  const boldPattern = /\*\*(.+?)\*\*/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let keyIndex = 0;

  while ((match = boldPattern.exec(cleanText)) !== null) {
    if (match.index > lastIndex) {
      parts.push(cleanText.slice(lastIndex, match.index));
    }
    parts.push(
      <strong key={`bold-${keyIndex++}`} className={isCyber ? "text-cyan-300" : "text-divine-gold"}>
        {match[1]}
      </strong>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < cleanText.length) {
    parts.push(cleanText.slice(lastIndex));
  }

  return parts.length > 0 ? parts : cleanText;
};

// Helper to render INSIGHT text with markdown ## headings and **bold**
const renderInsightText = (text, isCyber) => {
  if (!text) return null;
  
  // Clean the text first
  let cleanText = cleanCoachText(text);
  
  // Split by lines to handle headings
  const lines = cleanText.split('\n');
  const result = [];
  let keyIndex = 0;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Check for ## heading
    if (line.startsWith('## ')) {
      const headingText = line.slice(3).trim();
      result.push(
        <h3 key={`heading-${keyIndex++}`} className={`font-semibold text-base mt-3 mb-1 ${isCyber ? "text-cyan-300" : "text-divine-gold"}`}>
          {stripEmojis(headingText)}
        </h3>
      );
      continue;
    }
    
    // Check for # heading
    if (line.startsWith('# ')) {
      const headingText = line.slice(2).trim();
      result.push(
        <h2 key={`heading-${keyIndex++}`} className={`font-bold text-lg mt-3 mb-1 ${isCyber ? "text-cyan-300" : "text-divine-gold"}`}>
          {stripEmojis(headingText)}
        </h2>
      );
      continue;
    }
    
    // Parse bold in regular lines
    const boldPattern = /\*\*(.+?)\*\*/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = boldPattern.exec(line)) !== null) {
      if (match.index > lastIndex) {
        parts.push(line.slice(lastIndex, match.index));
      }
      parts.push(
        <strong key={`bold-${keyIndex++}`} className={isCyber ? "text-cyan-300" : "text-divine-gold"}>
          {match[1]}
        </strong>
      );
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < line.length) {
      parts.push(line.slice(lastIndex));
    }
    
    // Add the line with newline
    if (parts.length > 0) {
      result.push(<span key={`line-${keyIndex++}`}>{parts}</span>);
    } else {
      result.push(<span key={`line-${keyIndex++}`}>{line}</span>);
    }
    
    // Add newline between lines (except last)
    if (i < lines.length - 1) {
      result.push(<br key={`br-${keyIndex++}`} />);
    }
  }

  return result.length > 0 ? result : cleanText;
};

const CoachingPanel = ({ 
  isOpen, 
  onClose, 
  personaMessage, 
  userMessage,
  messageId,  // Unique identifier for this persona message
  personaId, 
  personaName,
  relationshipStage = 3,
  onUseInChat,  // Callback to insert text into main chat input
  onCoachingGenerated,  // Callback when coaching tips are generated (for inline display)
  onTokensUpdate,  // Callback to notify parent of token updates
  userDraft = "",  // User's draft response for co-creation
  sessionId = null,  // Parent chat session ID for linking
  matchTextingStyle = null,  // Match's preferred texting style
  // Escalation mode props
  isEscalationMode = false,
  difficulty = "medium",
  currentXp = 50,
  currentLevel = 1,
  trend = "stable",
  upstreak = 0,
  downstreak = 0
}) => {
  const { isCyber } = useTheme();
  const [loading, setLoading] = useState(false);
  const [coachingData, setCoachingData] = useState(null);
  const [annotations, setAnnotations] = useState([]);  // New: V2 annotations array
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [sendingChat, setSendingChat] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState("");  // For "Selected" indicator (only on ArrowRight)
  const [highlightedResponseIdx, setHighlightedResponseIdx] = useState(null);  // For visual highlight (ArrowDown)
  const [currentMessageId, setCurrentMessageId] = useState(null);
  const [maxWordsOption, setMaxWordsOption] = useState(() => {
    // Restore from localStorage or default to "med"
    const saved = localStorage.getItem("coaching_max_words_option");
    return saved || "med";
  }); // short=50, med=100, long=150
  const [inputExpanded, setInputExpanded] = useState(false); // For expandable input
  const [capturedDraft, setCapturedDraft] = useState("");  // Capture draft when panel opens
  const [editedResponses, setEditedResponses] = useState({});  // Track edited responses {index: editedText}
  const [helperMessageShown, setHelperMessageShown] = useState(false);  // Track if helper message was shown
  const [showTextingStyleTip, setShowTextingStyleTip] = useState(false);  // Show texting style tip when max_words changes
  const [stateHash, setStateHash] = useState(null);  // State hash from coach panel V2
  const [stateChanged, setStateChanged] = useState(false);  // Whether state changed since last call
  
  // Max words mapping
  const maxWordsMap = { short: 50, med: 100, long: 150 };
  const maxWords = maxWordsMap[maxWordsOption] || 100;
  
  // Coaching Session state
  const [coachingSessionId, setCoachingSessionId] = useState(null);
  const [sessionMessageCount, setSessionMessageCount] = useState(0);
  const [cumulativeTokens, setCumulativeTokens] = useState(0);  // Track total tokens used in this session
  
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const coachingChatInputRef = useRef(null);  // Ref for coaching panel chat input
  const contentTopRef = useRef(null);  // Ref for scrolling to top of content

  // Track if we should force regenerate due to draft change
  const prevDraftRef = useRef("");
  const prevMessageIdRef = useRef(null);

  // Initialize or load coaching when panel opens or message/draft changes
  useEffect(() => {
    if (isOpen && messageId && personaMessage) {
      const currentDraft = userDraft || "";
      const isNewMessage = messageId !== prevMessageIdRef.current;
      const hasDraftChanged = currentDraft !== prevDraftRef.current;
      const hasDraft = currentDraft.trim().length > 0;
      
      // Update refs
      prevMessageIdRef.current = messageId;
      prevDraftRef.current = currentDraft;
      setCapturedDraft(currentDraft);
      
      if (isNewMessage) {
        // New message - clear previous data including coaching session
        setCoachingData(null);
        setAnnotations([]);  // Reset annotations
        setChatHistory([]);
        setChatInput("");
        setSelectedResponse("");
        setHighlightedResponseIdx(null);
        setEditedResponses({});  // Clear any edited responses
        setCurrentMessageId(messageId);
        setCoachingSessionId(null);  // Reset coaching session
        setSessionMessageCount(0);
        setHelperMessageShown(false);  // Reset helper message for new session
        // Create new coaching session
        initOrLoadCoaching(hasDraft, currentDraft);
      } else if (hasDraftChanged && hasDraft) {
        // Same message but draft changed - FULL RELOAD with new draft
        setEditedResponses({});
        setSelectedResponse("");
        setHighlightedResponseIdx(null);
        initOrLoadCoaching(true, currentDraft);
      }
    }
  }, [isOpen, messageId, personaMessage, userDraft]);

  // Reset refs when panel closes
  useEffect(() => {
    if (!isOpen) {
      prevDraftRef.current = "";
      prevMessageIdRef.current = null;
    }
  }, [isOpen]);

  // Save maxWordsOption to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("coaching_max_words_option", maxWordsOption);
  }, [maxWordsOption]);

  // Scroll to bottom of chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory]);

  const initOrLoadCoaching = async (forceRegenerate = false, draftText = "") => {
    if (!personaMessage) return;
    
    setLoading(true);
    try {
      // Use Coach Panel V2 API
      const panelRequest = {
        sim_session_id: sessionId || `temp_${Date.now()}`,
        match_id: personaId,
        user_draft: draftText || userDraft || "",
        max_words: maxWords
      };
      
      console.log("[CoachPanel V2] Requesting coaching:", panelRequest);
      
      const response = await authAxios.post("/v2/coaching/panel/event", panelRequest);
      
      if (!response.data.success) {
        throw new Error(response.data.error || "Coach panel request failed");
      }
      
      // Store session info from V2 response
      setCoachingSessionId(response.data.coach_session_id);
      setStateHash(response.data.state_hash);
      setStateChanged(response.data.state_changed);
      
      // V2 response already has the correct structure
      const insights = response.data.insights || "";
      const annotations = response.data.annotations || [];
      const suggestions = response.data.suggestions || [];
      
      // Set annotations
      setAnnotations(annotations);
      
      // Clear edited responses and selection when new suggestions arrive
      setEditedResponses({});
      setSelectedResponse(null);
      setHighlightedResponseIdx(null);
      
      // Format coaching data for display
      setCoachingData({
        coaching_id: response.data.coach_session_id,
        coach_insight: insights,
        suggested_responses: suggestions,
        stage_label: `Stage ${relationshipStage}`,
        persona_name: personaName,
        relationship_stage: relationshipStage,
        is_saved_state: false,
        output_format: "json"
      });
      
      // Build coach response text for chat history
      const coachResponseText = insights || "Coaching analysis complete.";
      
      // Add initial coach message to chat history
      const initialMessages = [{
        role: "assistant",
        content: coachResponseText,
        timestamp: new Date().toISOString()
      }];
      
      // Add helper message once at session start
      if (!helperMessageShown) {
        initialMessages.push({
          role: "assistant",
          content: "If you would like me to reframe, or alter these responses with your added input, please let me know.",
          timestamp: new Date().toISOString(),
          isHelper: true
        });
        setHelperMessageShown(true);
      }
      
      setChatHistory(initialMessages);
      
      toast.success(`Coaching ready`);
      
    } catch (error) {
      console.error("[CoachPanel V2] Error:", error);
      console.error("Error response:", error.response?.data);
      toast.error(error.response?.data?.error || "Failed to load coaching");
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    setLoading(true);
    try {
      // Clear local state - FULL RELOAD
      setCoachingData(null);
      setChatHistory([]);
      setChatInput("");
      setSelectedResponse("");
      setEditedResponses({});  // Clear edited responses on full reload
      setHighlightedResponseIdx(null);
      setShowTextingStyleTip(false);  // Hide tip after regenerate
      
      // Generate new coaching with current word limit and captured draft
      console.log(`[CoachPanel V2] Regenerating with word limit: ${maxWords}`);
      await initOrLoadCoaching(true, capturedDraft);
      toast.success("Coaching regenerated!");
    } catch (error) {
      console.error("[CoachPanel V2] Error regenerating:", error);
      toast.error("Failed to regenerate");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyResponse = (text) => {
    // If text is already clean (from edited state), use as-is
    const cleanText = text.includes('**') ? cleanCoachText(text) : text;
    navigator.clipboard.writeText(cleanText);
    toast.success("Copied to clipboard!");
  };

  const handleSelectResponse = (text) => {
    const cleanText = text.includes('**') ? cleanCoachText(text) : text;
    setChatInput(cleanText);
    setSelectedResponse(cleanText);
    inputRef.current?.focus();
  };

  const handleSendChat = async () => {
    if (!chatInput.trim() || !coachingData) return;
    
    const userMsg = chatInput.trim();
    setChatInput("");
    setSelectedResponse("");
    setSendingChat(true);
    
    // Add user message to history optimistically
    setChatHistory(prev => [...prev, { role: "user", content: userMsg }]);
    
    try {
      let response;
      
      // Use coaching session endpoint if we have a session
      if (coachingSessionId) {
        response = await authAxios.post(`/v2/coaching/session/${coachingSessionId}/chat`, {
          message: userMsg,
          max_words: maxWords,
          output_format: "json"  // Request structured JSON response
        });
        
        // Update session message count
        setSessionMessageCount(response.data.message_count || sessionMessageCount + 2);
        
        // Parse V2 unified response
        const parsedResponse = parseV2CoachingResponse(response.data.coach_response);
        
        // Add coach response to history (use raw response for chat display)
        setChatHistory(prev => [...prev, { 
          role: "assistant", 
          content: parsedResponse.insights || cleanJsonFromText(response.data.coach_insight || response.data.coach_response),
          cadence: response.data.cadence_info
        }]);
        
        // Update annotations from parsed response
        if (parsedResponse.annotations?.length > 0) {
          setAnnotations(parsedResponse.annotations);
        }
        
        // Get cleaned insight and suggestions from parsed response
        const cleanedInsight = parsedResponse.insights 
          || (response.data.coach_insight ? cleanJsonFromText(response.data.coach_insight) : null);
        
        const newSuggestions = parsedResponse.suggestions.length > 0 
          ? parsedResponse.suggestions 
          : response.data.suggested_responses;
        
        if (cleanedInsight || newSuggestions?.length > 0) {
          setCoachingData(prev => ({
            ...prev,
            // Replace insights with new ones (append with separator)
            ...(cleanedInsight && { 
              coach_insight: prev.coach_insight 
                ? prev.coach_insight + "\n\n---\n\n" + cleanedInsight 
                : cleanedInsight 
            }),
            // Replace suggestions with new ones
            ...(newSuggestions?.length > 0 && { 
              suggested_responses: newSuggestions 
            })
          }));
          // Clear edited responses and highlight when new suggestions arrive
          if (newSuggestions?.length > 0) {
            setEditedResponses({});
            setHighlightedResponseIdx(null);
          }
        }
        
        // Update token tracking
        if (response.data.tokens_used) {
          const newTotal = cumulativeTokens + response.data.tokens_used;
          setCumulativeTokens(newTotal);
          const estimatedCost = (newTotal / 1000) * 0.003;
          if (onTokensUpdate) {
            onTokensUpdate(newTotal, estimatedCost);
          }
        }
        
        // Log cadence info
        if (response.data.cadence_info) {
          console.log(`[Coaching Session] MSG ${response.data.message_count}`, response.data.cadence_info);
        }
      } else {
        // Fall back to legacy chat endpoint
        response = await authAxios.post("/v2/coaching/chat", {
          coaching_id: coachingData.coaching_id,
          message_id: messageId,
          message: userMsg,
          persona_id: personaId,
          session_id: sessionId || null
        });
        
        // Add coach response to history
        setChatHistory(prev => [...prev, { role: "assistant", content: response.data.response }]);
        
        // Update suggestions if new ones provided
        if (response.data.suggested_responses?.length > 0) {
          setCoachingData(prev => ({
            ...prev,
            suggested_responses: response.data.suggested_responses
          }));
        }
      }
    } catch (error) {
      console.error("Error in coaching chat:", error);
      toast.error("Failed to send message");
      // Remove the optimistic user message on error
      setChatHistory(prev => prev.slice(0, -1));
    } finally {
      setSendingChat(false);
      // Auto-focus input after response
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendChat();
    }
  };

  // Close panel (save disabled)
  const handleClose = () => {
    // Save disabled - just close
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={handleClose}
      />
      
      {/* Panel - LEFT SIDE - Solid background matching main chat */}
      <div className={`fixed left-0 top-0 h-full w-full max-w-md z-50 transform transition-transform duration-300 ${
        isOpen ? "translate-x-0" : "-translate-x-full"
      } ${isCyber ? "bg-[#0f172a]" : "bg-[#1a1a2e]"} border-r ${
        isCyber ? "border-cyan-500/30" : "border-divine-gold/30"
      } flex flex-col`}>
        
        {/* Header - Solid background */}
        <div className={`flex-shrink-0 p-4 border-b ${
          isCyber ? "border-cyan-500/20 bg-[#0f172a]" : "border-divine-gold/20 bg-[#1a1a2e]"
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div>
                <h2 className={`font-semibold ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}>
                  Agent Eros Advice
                </h2>
                <p className="text-white/50 text-xs">
                  {isEscalationMode ? "Escalation Mode" : "For the selected message"}
                  {stateHash && <span className="ml-2 opacity-50">#{stateHash}</span>}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Max Words Dropdown */}
              <div className="flex items-center gap-1">
                <span className="text-white/50 text-xs">Length:</span>
                <select
                  value={maxWordsOption}
                  onChange={(e) => {
                    setMaxWordsOption(e.target.value);
                    setShowTextingStyleTip(true);
                    setTimeout(() => contentTopRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
                  }}
                  className={`px-2 py-1 rounded text-xs ${
                    isCyber 
                      ? "bg-slate-800 border border-cyan-500/30 text-cyan-400" 
                      : "bg-divine-charcoal border border-divine-gold/30 text-divine-gold"
                  } focus:outline-none cursor-pointer`}
                  title="Response length"
                >
                  <option value="short">Short (50)</option>
                  <option value="med">Med (100)</option>
                  <option value="long">Long (150)</option>
                </select>
              </div>
              
              {coachingData && (
                <button
                  onClick={handleRegenerate}
                  disabled={loading}
                  className={`p-2 rounded-lg transition-colors ${
                    isCyber ? "hover:bg-cyan-500/20" : "hover:bg-divine-gold/20"
                  }`}
                  title="Regenerate with new length"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""} ${
                    isCyber ? "text-cyan-400" : "text-divine-gold"
                  }`} />
                </button>
              )}
              <button
                onClick={handleClose}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5 text-white/70" />
              </button>
            </div>
          </div>
          
          {/* Texting Style Tip - shown when max_words changes */}
          {showTextingStyleTip && matchTextingStyle && (
            <div className={`mt-3 p-2 rounded text-xs ${
              isCyber ? "bg-cyan-500/10 border border-cyan-500/20" : "bg-divine-gold/10 border border-divine-gold/20"
            }`}>
              <span className="text-white/70">💡 </span>
              <span className={isCyber ? "text-cyan-300" : "text-divine-gold"}>
                {personaName}&apos;s Preferred Texting Style:
              </span>
              <span className="text-white/80 ml-1">{matchTextingStyle}</span>
              <span className="text-white/50 ml-2">• Click refresh to apply new length</span>
            </div>
          )}
        </div>
        
        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className={`animate-spin w-8 h-8 border-2 rounded-full ${
                isCyber ? "border-cyan-500 border-t-transparent" : "border-divine-gold border-t-transparent"
              }`} />
            </div>
          ) : coachingData ? (
            <div className="p-4 space-y-4">
              {/* Top anchor for scroll */}
              <div ref={contentTopRef} />
              
              {/* Section 0: User's Draft (if present) */}
              {capturedDraft && (
                <div className={`p-4 rounded-lg ${
                  isCyber ? "bg-purple-500/10 border border-purple-500/30" : "bg-amber-500/10 border border-amber-500/30"
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className={`font-medium text-sm ${
                      isCyber ? "text-purple-400" : "text-amber-400"
                    }`}>Your Draft</h3>
                    <span className="text-white/40 text-xs">(co-creating from your idea)</span>
                  </div>
                  <p className="text-white/70 text-sm italic">&ldquo;{capturedDraft}&rdquo;</p>
                </div>
              )}
              
              {/* Section 1: Message Insights - No suggested responses here */}
              <div className={`p-4 rounded-lg ${
                isCyber ? "bg-slate-800/50" : "bg-white/5"
              }`}>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className={`font-medium text-sm ${
                    isCyber ? "text-cyan-400" : "text-divine-gold"
                  }`}>Message Insights</h3>
                </div>
                <div className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap">
                  {renderInsightText(coachingData.coach_insight, isCyber)}
                </div>
              </div>
              
              {/* Section 1.5: Annotations (V2 - if present) */}
              {annotations && annotations.length > 0 && (
                <div className={`p-4 rounded-lg ${
                  isCyber ? "bg-slate-800/50" : "bg-white/5"
                }`}>
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className={`font-medium text-sm ${
                      isCyber ? "text-cyan-400" : "text-divine-gold"
                    }`}>Coach Observations</h3>
                  </div>
                  <div className="space-y-3">
                    {annotations.map((annotation, idx) => {
                      // Color coding based on annotation type
                      const typeColors = {
                        positive: isCyber ? "border-green-500/40 bg-green-500/10" : "border-emerald-500/40 bg-emerald-500/10",
                        opportunity: isCyber ? "border-cyan-500/40 bg-cyan-500/10" : "border-divine-gold/40 bg-divine-gold/10",
                        caution: isCyber ? "border-amber-500/40 bg-amber-500/10" : "border-amber-500/40 bg-amber-500/10",
                        neutral: isCyber ? "border-slate-500/40 bg-slate-500/10" : "border-white/20 bg-white/5"
                      };
                      const typeIcons = {
                        positive: "✓",
                        opportunity: "→",
                        caution: "!",
                        neutral: "•"
                      };
                      const typeLabels = {
                        positive: "Positive Signal",
                        opportunity: "Opportunity",
                        caution: "Caution",
                        neutral: "Note"
                      };
                      const annotationType = annotation.type || "neutral";
                      
                      return (
                        <div 
                          key={idx} 
                          className={`p-3 rounded-lg border ${typeColors[annotationType] || typeColors.neutral}`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                              annotationType === "positive" ? "bg-green-500/20 text-green-400" :
                              annotationType === "opportunity" ? (isCyber ? "bg-cyan-500/20 text-cyan-400" : "bg-divine-gold/20 text-divine-gold") :
                              annotationType === "caution" ? "bg-amber-500/20 text-amber-400" :
                              "bg-white/10 text-white/60"
                            }`}>
                              {typeIcons[annotationType]} {typeLabels[annotationType]}
                            </span>
                          </div>
                          {annotation.observation && (
                            <p className="text-white/80 text-sm mb-1">
                              <span className="text-white/50">Observed: </span>
                              {annotation.observation}
                            </p>
                          )}
                          {annotation.insight && (
                            <p className="text-white/80 text-sm mb-1">
                              <span className="text-white/50">Insight: </span>
                              {annotation.insight}
                            </p>
                          )}
                          {annotation.suggestion && (
                            <p className={`text-sm mt-2 ${
                              isCyber ? "text-cyan-400/80" : "text-divine-gold/80"
                            }`}>
                              <span className="text-white/50">Try: </span>
                              {annotation.suggestion}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Section 2: Suggested Responses */}
              <div className={`p-4 rounded-lg ${
                isCyber ? "bg-slate-800/50" : "bg-white/5"
              }`}>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className={`font-medium text-sm ${
                    isCyber ? "text-cyan-400" : "text-divine-gold"
                  }`}>Suggested Responses</h3>
                </div>
                <div className="space-y-3">
                  {coachingData.suggested_responses?.map((response, idx) => (
                    <div key={idx}>
                      {/* Response Option Label */}
                      <div className={`text-xs font-medium mb-1 ${
                        isCyber ? "text-cyan-400/70" : "text-divine-gold/70"
                      }`}>
                        Response Option {idx + 1}
                      </div>
                      <div 
                        className={`group relative p-3 rounded-lg border cursor-pointer transition-all ${
                          (selectedResponse === cleanCoachText(response) || highlightedResponseIdx === idx)
                            ? isCyber 
                              ? "border-cyan-500 bg-cyan-500/10" 
                              : "border-divine-gold bg-divine-gold/10"
                            : isCyber
                              ? "border-cyan-500/20 hover:border-cyan-500/50 bg-slate-900/50 hover:bg-slate-800/50"
                              : "border-divine-gold/20 hover:border-divine-gold/50 bg-black/20 hover:bg-black/30"
                        }`}
                      >
                      {/* Editable response text - auto-resize to fit content */}
                      <textarea
                        className="w-full pr-10 text-white/80 text-sm leading-relaxed select-text cursor-text bg-transparent border-none resize-none focus:outline-none focus:ring-0 overflow-hidden"
                        value={editedResponses[idx] !== undefined ? editedResponses[idx] : cleanCoachText(response)}
                        onChange={(e) => {
                          // Auto-resize on change
                          e.target.style.height = '0';
                          e.target.style.height = e.target.scrollHeight + 'px';
                          setEditedResponses(prev => ({
                            ...prev,
                            [idx]: e.target.value
                          }));
                        }}
                        ref={(el) => {
                          // Auto-resize on mount/update
                          if (el) {
                            requestAnimationFrame(() => {
                              el.style.height = '0';
                              el.style.height = el.scrollHeight + 'px';
                            });
                          }
                        }}
                        style={{ minHeight: '20px' }}
                      />
                      {/* Copy button - top right */}
                      <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-all">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const textToCopy = editedResponses[idx] !== undefined ? editedResponses[idx] : cleanCoachText(response);
                            handleCopyResponse(textToCopy);
                          }}
                          className={`p-1.5 rounded ${
                            isCyber 
                              ? "bg-cyan-500/20 hover:bg-cyan-500/40" 
                              : "bg-divine-gold/20 hover:bg-divine-gold/40"
                          }`}
                          title="Copy to clipboard"
                        >
                          <Copy className={`w-3.5 h-3.5 ${
                            isCyber ? "text-cyan-400" : "text-divine-gold"
                          }`} />
                        </button>
                      </div>
                      {/* Send to persona chat - center right */}
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onUseInChat) {
                              const textToSend = editedResponses[idx] !== undefined ? editedResponses[idx] : cleanCoachText(response);
                              const wasEdited = editedResponses[idx] !== undefined && editedResponses[idx] !== cleanCoachText(response);
                              onUseInChat(textToSend, { wasEditedInPanel: wasEdited });
                              toast.success(wasEdited ? "Edited text sent to chat" : "Sent to chat input");
                              onClose();
                            }
                          }}
                          className={`p-1.5 rounded ${
                            isCyber 
                              ? "bg-cyan-500/20 hover:bg-cyan-500/40" 
                              : "bg-divine-gold/20 hover:bg-divine-gold/40"
                          }`}
                          title="Send to persona chat"
                        >
                          <ArrowRight className={`w-3.5 h-3.5 ${
                            isCyber ? "text-cyan-400" : "text-divine-gold"
                          }`} />
                        </button>
                      </div>
                      {/* Send to Ask Agent Eros chat input - bottom right */}
                      <div className="absolute right-2 bottom-2 opacity-0 group-hover:opacity-100 transition-all">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const textToSelect = editedResponses[idx] !== undefined ? editedResponses[idx] : cleanCoachText(response);
                            // Wrap in quotes and insert into coaching chat input
                            const quotedText = `"${textToSelect}"`;
                            setChatInput(quotedText);
                            // Set highlight but NOT selected (no "Selected" indicator)
                            setHighlightedResponseIdx(idx);
                            setSelectedResponse("");  // Clear selected state
                            // Focus and place cursor at beginning
                            setTimeout(() => {
                              if (coachingChatInputRef.current) {
                                coachingChatInputRef.current.focus();
                                coachingChatInputRef.current.setSelectionRange(0, 0);
                              }
                            }, 50);
                          }}
                          className={`p-1.5 rounded ${
                            isCyber 
                              ? "bg-cyan-500/20 hover:bg-cyan-500/40" 
                              : "bg-divine-gold/20 hover:bg-divine-gold/40"
                          }`}
                          title="Send to Ask Agent Eros"
                        >
                          <ArrowDown className={`w-3.5 h-3.5 ${
                            isCyber ? "text-cyan-400" : "text-divine-gold"
                          }`} />
                        </button>
                      </div>
                      {/* Selection indicator - only show for ArrowRight selection, not ArrowDown */}
                      {selectedResponse === (editedResponses[idx] !== undefined ? editedResponses[idx] : cleanCoachText(response)) && (
                        <div className={`absolute left-2 bottom-2 px-1.5 py-0.5 rounded text-xs ${
                          isCyber ? "bg-cyan-500/20 text-cyan-400" : "bg-divine-gold/20 text-divine-gold"
                        }`}>
                          Selected
                        </div>
                      )}
                    </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Section 3: Coach Chat */}
              <div className={`p-4 rounded-lg ${
                isCyber ? "bg-slate-800/50" : "bg-white/5"
              }`}>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className={`font-medium text-sm ${
                    isCyber ? "text-cyan-400" : "text-divine-gold"
                  }`}>Agent Eros Chat</h3>
                  {chatHistory.length > 0 && (
                    <span className="text-white/40 text-xs">({chatHistory.length} messages)</span>
                  )}
                </div>
                
                {/* Chat history */}
                {chatHistory.length > 0 && (
                  <div className="max-h-48 overflow-y-auto mb-3 space-y-2">
                    {chatHistory.map((msg, idx) => (
                      <div 
                        key={idx}
                        className={`p-2 rounded-lg text-sm ${
                          msg.role === "user"
                            ? isCyber
                              ? "bg-cyan-500/20 text-cyan-100 ml-4"
                              : "bg-divine-gold/20 text-divine-gold ml-4"
                            : "bg-white/5 text-white/80 mr-4"
                        }`}
                      >
                        <span className={`text-xs font-medium block mb-1 ${
                          msg.role === "user" 
                            ? isCyber ? "text-cyan-400" : "text-divine-gold"
                            : "text-white/50"
                        }`}>
                          {msg.role === "user" ? "You" : "Eros"}
                        </span>
                        <div className="whitespace-pre-wrap">
                          {renderBoldText(msg.content, isCyber)}
                        </div>
                      </div>
                    ))}
                    <div ref={chatEndRef} />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-white/50">
              No coaching data available
            </div>
          )}
        </div>
        
        {/* Chat Input - Fixed at bottom */}
        {coachingData && (
          <div className={`flex-shrink-0 p-4 border-t ${
            isCyber ? "border-cyan-500/20 bg-[#0f172a]" : "border-divine-gold/20 bg-[#1a1a2e]"
          }`}>
            <div className="flex flex-col gap-2">
              <div className="relative">
                <textarea
                  ref={(el) => {
                    inputRef.current = el;
                    coachingChatInputRef.current = el;
                  }}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask Agent Eros..."
                  className={`w-full px-3 py-2 pr-10 rounded-lg text-sm resize-none transition-all ${
                    isCyber 
                      ? "bg-slate-900 border border-cyan-500/30 text-white placeholder-white/30 focus:border-cyan-500/60"
                      : "bg-black/30 border border-divine-gold/30 text-white placeholder-white/30 focus:border-divine-gold/60"
                  } focus:outline-none`}
                  rows={inputExpanded ? 6 : 2}
                />
                <button
                  onClick={() => setInputExpanded(!inputExpanded)}
                  className={`absolute right-2 top-2 p-1 rounded transition-all opacity-60 hover:opacity-100 ${
                    isCyber ? "hover:bg-cyan-500/20" : "hover:bg-divine-gold/20"
                  }`}
                  title={inputExpanded ? "Collapse input" : "Expand input"}
                >
                  {inputExpanded ? (
                    <Minimize2 className={`w-4 h-4 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                  ) : (
                    <Maximize2 className={`w-4 h-4 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                  )}
                </button>
              </div>
              <button
                onClick={handleSendChat}
                disabled={sendingChat || !chatInput.trim()}
                className={`w-full py-2 rounded-lg transition-all flex items-center justify-center gap-2 ${
                  sendingChat || !chatInput.trim()
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                } ${
                  isCyber 
                    ? "bg-cyan-500 hover:bg-cyan-400 text-black"
                    : "bg-divine-gold hover:bg-divine-gold/80 text-black"
                }`}
              >
                {sendingChat ? (
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span className="text-sm font-medium">Send to Agent Eros</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CoachingPanel;
